// src/components/Dashboard/Dashboard.js
import React, { useEffect, useState, useRef } from "react";
import "./Dashboard.css";
import { Link, useNavigate } from "react-router-dom";
import { FaBell, FaStore, FaBoxes } from "react-icons/fa";
import Sidebar from "../Sidebar/Sidebar";
import api, { cloudApi } from "../../api";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";
import { jwtDecode } from "jwt-decode";

const ENV = process.env.REACT_APP_ENV || "production";
const IS_DESKTOP = ENV === "desktop";
const NETWORK_TIMEOUT_MS = 10000;

const Dashboard = () => {
  const [pharmacyName, setPharmacyName] = useState("");
  const [profile, setProfile] = useState({ full_name: "", avatar: "" });
  const [verificationStatus, setVerificationStatus] = useState("");
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [topDrugs, setTopDrugs] = useState([]);
  const [overview, setOverview] = useState({
    total_inventory_items: 0,
    total_unique_drugs: 0,
    total_quantity: 0,
  });
  const [userType, setUserType] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [verificationSyncing, setVerificationSyncing] = useState(false);

  // ── Sync pending alert (desktop only) ──
  const [syncAlert, setSyncAlert] = useState(null);

  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const isPharmacyOwner = userType === "PHARMACY";
  const isPharmacyStaff = userType === "PHARMACYSTAFF";
  const isPharmacyBranch = userType === "PHARMACYBRANCH";

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    const initializeDashboard = async () => {
      const token = localStorage.getItem("access_token");
      if (!token) return navigate("/Login");

      try {
        if (api.refreshTokenIfNeeded) {
          await api.refreshTokenIfNeeded();
        }

        const refreshedToken = localStorage.getItem("access_token");
        let decoded = null;
        try {
          decoded = refreshedToken ? jwtDecode(refreshedToken) : null;
        } catch {
          // token decode failure — proceed with null decoded
        }

        const type = decoded?.user_type || "UNKNOWN";
        setUserType(type);

        const corePromises = [
          fetchPharmacyProfile(),
          fetchNotifications(),
        ];

        if (
          type === "PHARMACY" ||
          type === "PHARMACYSTAFF" ||
          type === "PHARMACYBRANCH"
        ) {
          corePromises.push(fetchPharmacyOverview());
        }

        const shouldFetchTopDrugs = IS_DESKTOP ? navigator.onLine : true;

        const allPromises = shouldFetchTopDrugs
          ? [...corePromises, fetchTopDrugs()]
          : corePromises;

        if (!IS_DESKTOP) {
          await Promise.race([
            Promise.allSettled(allPromises),
            new Promise((_, reject) =>
              setTimeout(
                () => reject(new Error("NETWORK_TIMEOUT")),
                NETWORK_TIMEOUT_MS
              )
            ),
          ]).catch((err) => {
            if (err.message === "NETWORK_TIMEOUT") {
              setErrorMsg(
                "⚠️ Network is slow or unavailable. Some data may not have loaded. Please check your connection and refresh."
              );
            }
          });
        } else {
          await Promise.allSettled(allPromises);
        }
      } catch {
        setErrorMsg("Failed to load dashboard data.");
      } finally {
        setIsLoading(false);
      }
    };

    initializeDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* =========================
     Poll sync pending alert (desktop only)
     Checks every 5 minutes if there are unsynced transactions
     older than 2 hours — shows a summary banner on the dashboard
     so the owner can see sync health at a glance.
     ========================= */
  useEffect(() => {
    if (!IS_DESKTOP) return;

    const checkSyncAlert = async () => {
      try {
        const res = await api.get("/sync/pending-alert");
        if (res.data.alert) {
          setSyncAlert(res.data);
        } else {
          setSyncAlert(null);
        }
      } catch {
        // non-fatal — alert just won't show
      }
    };

    checkSyncAlert();
    const interval = setInterval(checkSyncAlert, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await cloudApi.get("/users/pharmacy/notifications");
      setNotifications(res.data);
      setUnreadCount(res.data.filter((n) => !n.is_read).length);
    } catch {
      // non-fatal — notifications just won't show
    }
  };

  const handleNotificationClick = async (notification) => {
    const { id, link } = notification;
    try {
      await cloudApi.put(`/users/pharmacy/notifications/${id}/read`);
      const updated = notifications.map((n) =>
        n.id === id ? { ...n, is_read: true } : n
      );
      setNotifications(updated);
      setUnreadCount(updated.filter((n) => !n.is_read).length);
      if (link) navigate(link);
    } catch {
      // non-fatal — notification mark-as-read failure
    }
  };

  const fetchPharmacyProfile = async () => {
    try {
      const res = await api.get("/users/profile");
      const profileData = res.data.profile;
      const userName = profileData.name || "User";
      setPharmacyName(profileData.pharmacy_name || "Pharmacy");
      setVerificationStatus(profileData.verification_status || "");

      let avatarUrl = "";

      if (IS_DESKTOP) {
        try {
          const cloudRes = await cloudApi.get("/users/profile");
          avatarUrl = cloudRes.data.profile?.logo || "";
        } catch {
          avatarUrl = profileData.logo || "";
        }
      } else {
        avatarUrl = profileData.logo || "";
      }

      if (
        avatarUrl &&
        (
          avatarUrl.includes("127.0.0.1") ||
          avatarUrl.includes("localhost") ||
          avatarUrl.startsWith("/static/")
        )
      ) {
        const filename = avatarUrl.split("/").pop();
        avatarUrl = `${process.env.REACT_APP_CLOUD_API_BASE_URL}/static/images/${filename}`;
      }

      if (!avatarUrl) {
        avatarUrl = `${process.env.REACT_APP_CLOUD_API_BASE_URL}/static/images/profile.png`;
      }

      setProfile({ full_name: userName, avatar: avatarUrl });
    } catch {
      // non-fatal — profile will show defaults
    }
  };

  const fetchTopDrugs = async () => {
    try {
      const res = await cloudApi.get("/pharmacies/drugs/popular");
      setTopDrugs(res.data.top_drugs || []);
    } catch {
      // non-fatal — top drugs just won't show
    }
  };

  const fetchPharmacyOverview = async () => {
    try {
      const res = await api.get("/pharmacies/pharmacy-overview");
      const overviewData = res.data.overview || {};
      setOverview({
        total_inventory_items: overviewData.total_inventory_items || 0,
        total_unique_drugs: overviewData.total_unique_drugs || 0,
        total_quantity: overviewData.total_quantity || 0,
      });
    } catch {
      // non-fatal — overview will show zeros
    }
  };

  const handleVerificationSync = async () => {
    setVerificationSyncing(true);
    try {
      if (IS_DESKTOP) {
        await api.post("/sync/now");
      } else {
        await api.post("/sync/pull");
      }
      await fetchPharmacyProfile();
    } catch {
      // non-fatal — sync will retry in background
    } finally {
      setVerificationSyncing(false);
    }
  };

  if (isLoading || userType === null) {
    return (
      <div className="dashboard-container">
        {!isMobile && <Sidebar />}
        <div className="main-dashboard">
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {!isMobile && <Sidebar />}
      <div className="main-dashboard">

        {IS_DESKTOP && !isOnline && (
          <div style={{
            background: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: "8px",
            padding: "0.75rem 1rem",
            marginBottom: "1rem",
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
            fontSize: "0.9rem",
          }}>
            <span style={{ fontSize: "1.3rem" }}>📡</span>
            <div>
              <strong style={{ color: "#856404" }}>You are offline</strong>
              <p style={{ margin: 0, color: "#856404", fontSize: "0.85rem" }}>
                Dashboard is showing local data. Some features like Top Drugs
                require an internet connection.
              </p>
            </div>
          </div>
        )}

        {/* ── Sync pending alert banner (desktop only) ──
            Quieter than POS banner — owner-facing summary.
            Shows how many transactions are pending and how long
            they have been waiting. */}
        {IS_DESKTOP && syncAlert && (
          <div style={{
            background: !isOnline ? "#fff3cd" : "#d1ecf1",
            border: `1px solid ${!isOnline ? "#ffc107" : "#bee5eb"}`,
            borderRadius: "8px",
            padding: "0.75rem 1rem",
            marginBottom: "1rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "0.75rem",
            fontSize: "0.9rem",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
              <span style={{ fontSize: "1.3rem" }}>
                {!isOnline ? "⚠️" : "🔄"}
              </span>
              <div>
                <strong style={{ color: !isOnline ? "#856404" : "#0c5460", display: "block" }}>
                  {!isOnline
                    ? "Transactions Pending Sync — Connect to Internet"
                    : `${syncAlert.pending_count} Transaction(s) Syncing...`}
                </strong>
                <p style={{ margin: 0, color: !isOnline ? "#856404" : "#0c5460", fontSize: "0.85rem" }}>
                  {syncAlert.message}
                </p>
              </div>
            </div>
            {!isOnline && (
              <button
                onClick={async () => {
                  try {
                    await api.post("/sync/now");
                    const res = await api.get("/sync/pending-alert");
                    setSyncAlert(res.data.alert ? res.data : null);
                  } catch {
                    // non-fatal
                  }
                }}
                style={{
                  padding: "0.4rem 1rem",
                  background: "#ffc107",
                  color: "#333",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                🔄 Retry Sync
              </button>
            )}
          </div>
        )}

        {errorMsg && (
          <div style={{
            background: "#fff5f5",
            border: "1px solid #dc3545",
            borderRadius: "8px",
            padding: "0.75rem 1rem",
            marginBottom: "1rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "0.75rem",
            fontSize: "0.9rem",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
              <span style={{ fontSize: "1.3rem" }}>⚠️</span>
              <p style={{ margin: 0, color: "#dc3545" }}>{errorMsg}</p>
            </div>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: "0.4rem 1rem",
                background: "#dc3545",
                color: "white",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
                fontSize: "0.85rem",
                whiteSpace: "nowrap",
              }}
            >
              🔄 Retry
            </button>
          </div>
        )}

        {/* Topbar */}
        <div className="dashboard-topbar">
          <h2>
            <FaStore /> {pharmacyName || "Pharmacy Dashboard"}
          </h2>

          <div className="profile-bar">
            <div className="notification-wrapper" ref={dropdownRef}>
              <div
                className="notification-icon"
                onClick={() => setShowDropdown(!showDropdown)}
              >
                <FaBell
                  size={18}
                  color={unreadCount > 0 ? "#ff0000" : "#ffc107"}
                />
                {unreadCount > 0 && (
                  <span className="notification-badge">{unreadCount}</span>
                )}
              </div>

              {showDropdown && (
                <div
                  className={`notification-dropdown ${
                    isMobile ? "mobile-dropdown" : ""
                  }`}
                >
                  <div className="notification-header">
                    <h4>Notifications</h4>
                    {isMobile && (
                      <button
                        className="close-dropdown-button"
                        onClick={() => setShowDropdown(false)}
                      >
                        ✖
                      </button>
                    )}
                  </div>
                  <div className="notification-list">
                    {notifications.length === 0 ? (
                      <p className="no-notification">No notifications</p>
                    ) : (
                      notifications.map((n) => (
                        <div
                          key={n.id}
                          className={`notification-item ${
                            n.is_read ? "read" : "unread"
                          }`}
                          onClick={() => handleNotificationClick(n)}
                          style={{ cursor: "pointer" }}
                        >
                          <p>{n.message}</p>
                          <span className="notif-date">
                            {new Date(n.created_at).toLocaleString()}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            <Link to="/PharmacyProfile" className="profile-link">
              <img
                src={profile.avatar}
                alt="Avatar"
                className="profile-avatar"
              />
              <span>{profile.full_name}</span>
            </Link>
          </div>
        </div>

        {/* Verification banners - PHARMACY only */}
        {isPharmacyOwner && (
          <>
            {verificationStatus === "NOT_APPLIED" && (
              <div className="verification-banner">
                <p>⚠️ Your pharmacy is not verified yet. Your drug listings are currently hidden from customers on the MyApoti mobile app..</p>
                <button
                  className="verify-button"
                  onClick={() => navigate("/PharmacyVerificationForm")}
                >
                  Verify Now
                </button>
              </div>
            )}

            {verificationStatus === "PENDING" && (
              <div className="verification-banner pending">
                <p>⏳ Your pharmacy verification is under review. Your drug will appear on drug listing for customers visibility on the MyApoti mobile app once your pharmacy is approved.</p>
                {!isMobile && (
                  <button
                    onClick={handleVerificationSync}
                    disabled={verificationSyncing}
                    style={{
                      marginTop: "0.5rem",
                      padding: "0.4rem 1rem",
                      background: verificationSyncing ? "#aaa" : "#007bff",
                      color: "white",
                      border: "none",
                      borderRadius: "5px",
                      cursor: verificationSyncing ? "not-allowed" : "pointer",
                      fontSize: "0.85rem",
                    }}
                  >
                    {verificationSyncing ? "🔄 Checking..." : "🔄 Check Approval Status"}
                  </button>
                )}
              </div>
            )}

            {verificationStatus === "REJECTED" && (
              <div className="verification-banner rejected">
                <p>❌ Your pharmacy verification was rejected. Please reapply.</p>
                <button
                  className="verify-button"
                  onClick={() => navigate("/PharmacyVerificationForm")}
                >
                  Reapply
                </button>
              </div>
            )}
          </>
        )}

        {/* Dashboard Cards */}
        <div className="dashboard-cards">
          <div className="card">
            <h3>Top Selling Drug on MyApoti</h3>
            {IS_DESKTOP && !isOnline ? (
              <p style={{ color: "#999", fontSize: "0.85rem" }}>
                📡 Internet required to load top drugs
              </p>
            ) : topDrugs.length === 0 ? (
              <p>No data available</p>
            ) : (
              <ul>
                {topDrugs.map((drug, index) => (
                  <li key={index}>
                    {drug.generic_name}{" "}
                    {drug.brand_name ? `(${drug.brand_name})` : ""}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {(isPharmacyOwner || isPharmacyStaff || isPharmacyBranch) && (
            <div className="card">
              <h3>Total Inventory</h3>
              <p>{overview.total_inventory_items}</p>
              <small>{overview.total_unique_drugs} Unique Drugs</small>
              <br />
              <small>{overview.total_quantity} Total Quantity</small>
            </div>
          )}

          <Link to="/POS" className="card clickable-card pos-card">
            <h3>🧾 New Sale (POS)</h3>
            <p>Sell drugs and generate receipts</p>
          </Link>

          {!isMobile && (
            <>
              {isPharmacyOwner && (
                <Link to="/ManageInventory" className="card clickable-card">
                  <h3>
                    <FaBoxes /> Manage Inventory
                  </h3>
                  <p>View and update all inventory items</p>
                </Link>
              )}

              {isPharmacyOwner && (
                <Link
                  to="/ManagePharmacyBranch"
                  className="card clickable-card"
                >
                  <h3>
                    <FaStore /> Manage Branches
                  </h3>
                  <p>View and update pharmacy branches</p>
                </Link>
              )}
            </>
          )}
        </div>

        {/* Info Section */}
        <div className="charts-container">
          <div className="chart-box">
            <h3>About MyApoti</h3>
            <p>
              MyApoti helps pharmacies manage inventory, track drug data, and
              maintain branches efficiently with analytics and alerts.
            </p>

            <ResponsiveContainer width="100%" height={250}>
              <BarChart
                data={[
                  { feature: "Inventory", value: 90 },
                  { feature: "Branches", value: 75 },
                  { feature: "Notifications", value: 85 },
                ]}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="feature" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="value" fill="#ffc107" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Dashboard;